// /src/services/api.ts

// What? A file for the API services.
// Why? To centralize API calls, promoting code reusability and simplifying data management across the application.
// How?
// - Defines custom hooks for each API endpoint, encapsulating the API call logic
// - Uses the useAuthFetch hook to make authenticated API calls
// - Returns memoized functions that execute the API requests
// - Provides type-safe interfaces for API responses using TypeScript
// - Centralizes API endpoint definitions, making it easier to manage and update
// - Allows components to easily fetch and manage data from the backend

import { useCallback } from "react";
import {
  FileType,
  User,
  UserWorkspacesType,
  WorkspaceUser,
  WorkspaceType,
  ResourceUsageData,
  UsageTrendTimeframe,
  UsageTrendData,
} from "../../../common/types";
import { SearchBKGResults } from "../../../common/types";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type {
  DigestConfig,
  DigestConfigCreate,
  DigestConfigUpdate,
  DigestResult,
} from "../types/digest";
import type { PDVCompanyConnections } from "../types";

// The backend may perform lane verification and one retry for each of PDV's
// resolve and connections requests. Its default worst-case budget is about 35
// seconds, so the browser must not abandon a request while those retries are
// still in progress.
export const PDV_COMPANY_CONNECTIONS_TIMEOUT_MS = 60_000;

/*
  Mock API Instructions:
  Endpoint for returning static data in the same format as the real APIs. For easy implementation in frontend rendering.
  Ignore dynamic endpoints that add, remove, or otherwise need to modify the database.

  Naming conventions:
    API endpoint name: /api/workspace/user/list
    Function name: workspaceUserList
    Hook name: useWorkspaceUserList
*/

/*
  User APIs
*/
// What? A hook for fetching the user's workspaces.
// Why? To retrieve the list of workspaces associated with the current user.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the workspaces.
export const useUserWorkspaces = () => {
  const authFetch = useAuthFetch();

  const userWorkspaces = async (): Promise<UserWorkspacesType[]> => {
    return (await authFetch(
      `/api/user/workspaces`,
      "list workspaces",
    )) as UserWorkspacesType[];
  };
  return userWorkspaces;
};

/*
  Workspace APIs
*/
// What? A hook for creating a new workspace.
// Why? To add a new workspace to the user's list of workspaces.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that creates a new workspace.
export const useWorkspaceCreate = () => {
  const authFetch = useAuthFetch();

  const workspaceCreate = async (
    workspaceName: string,
  ): Promise<WorkspaceType> => {
    return (await authFetch(`/api/workspace/create`, "create workspace", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: workspaceName,
      }),
    })) as WorkspaceType;
  };

  return workspaceCreate;
};

// What? A hook for reading a workspace by its ID.
// Why? To retrieve detailed information about a specific workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the workspace by ID.
export const useWorkspaceRead = () => {
  const authFetch = useAuthFetch();

  const workspaceRead = async (workspaceId: string): Promise<WorkspaceType> => {
    return (await authFetch(
      `/api/workspace/read?workspace_id=${workspaceId}`,
      "read workspace",
    )) as WorkspaceType;
  };

  return workspaceRead;
};

export const useWorkspaceUpdate = () => {
  const authFetch = useAuthFetch();

  const workspaceUpdate = async (
    workspaceId: string,
    workspaceData: {
      name: string;
      description: string;
      assistant_instructions: string;
    },
  ): Promise<WorkspaceType> => {
    return (await authFetch(
      `/api/workspace/update?workspace_id=${workspaceId}`,
      "update workspace",
      {
        method: "PUT", // PUT request for updates
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: workspaceData.name,
          description: workspaceData.description,
          assistant_instructions: workspaceData.assistant_instructions,
        }),
      },
    )) as WorkspaceType;
  };

  return workspaceUpdate;
};

// What? A hook for deleting a workspace by its ID.
// Why? To remove a workspace from the user's list of workspaces.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that deletes the workspace by ID.
export const useWorkspaceDelete = () => {
  const authFetch = useAuthFetch();

  const workspaceDelete = async (workspaceId: string): Promise<void> => {
    return await authFetch(
      `/api/workspace/delete?workspace_id=${workspaceId}`,
      "delete workspace",
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
  };

  return workspaceDelete;
};

/*
 Workspace User APIs
*/
// What? A hook for listing users in a workspace.
// Why? To retrieve the list of users associated with a specific workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the users in the workspace.
export const useWorkspaceUserList = () => {
  const authFetch = useAuthFetch();

  const workspaceUserList = async (
    workspaceId: string,
  ): Promise<WorkspaceUser[]> => {
    return (await authFetch(
      `/api/workspace/user/list?workspace_id=${workspaceId}`,
      "list workspace users",
    )) as WorkspaceUser[];
  };

  return workspaceUserList;
};

export const useWorkspaceUserInviteSend = () => {
  const authFetch = useAuthFetch();

  const workspaceUserInviteSend = async (
    workspaceId: string,
    email: string,
    role: string,
  ) => {
    return await authFetch(
      `/api/workspace/user/invite/send?workspace_id=${workspaceId}`,
      "send user invite",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          role: role,
        }),
      },
    );
  };

  return workspaceUserInviteSend;
};

/*
  Payment APIs
*/
// What? A hook for registering a payment for a workspace.
// Why? To record a payment made by a workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that registers the payment.
export const useWorkspacePaymentRegister = () => {
  const authFetch = useAuthFetch();

  const workspacePaymentRegister = async (
    workspaceId: string,
    subscription: string,
    amount: number,
  ) => {
    return await authFetch(
      `/api/workspace/payment/register?workspace_id=${workspaceId}`,
      "register payment",
      {
        method: "POST",
        body: JSON.stringify({
          subscription: subscription,
          amount: amount,
        }),
      },
    );
  };

  return workspacePaymentRegister;
};

export const useWorkspacePaymentList = () => {
  const authFetch = useAuthFetch();

  const workspacePaymentList = async (workspaceId: string) => {
    return await authFetch(
      `/api/workspace/payment/list?workspace_id=${workspaceId}`,
      "list payments",
    );
  };

  return workspacePaymentList;
};

/*
  Workspace user permissions
*/
// What? A hook for updating the role of a user in a workspace.
// Why? To modify the permissions or roles of a user within a workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that updates the user's role in the workspace.
export const useWorkspaceUserUpdate = () => {
  const authFetch = useAuthFetch();

  const workspaceUserUpdate = async (
    workspaceId: string,
    role: string,
    userId: string,
  ) => {
    return await authFetch(
      `/api/workspace/user/update?workspace_id=${workspaceId}`,
      "update user permissions",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          role: role,
          id: userId,
        }),
      },
    );
  };

  return workspaceUserUpdate;
};

// What? A hook for removing a user from a workspace.
// Why? To remove a user from a workspace, typically when the user is no longer associated with the workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that removes the user from the workspace.
export const useWorkspaceUserRemove = () => {
  const authFetch = useAuthFetch();

  const workspaceUserRemove = async (
    workspaceId: string,
    userId: string,
  ): Promise<void> => {
    return await authFetch(
      `/api/workspace/user/remove?workspace_id=${workspaceId}`,
      "remove user from workspace",
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id: userId }),
      },
    );
  };

  return workspaceUserRemove;
};

// What? A hook for accepting an invite to a workspace.
// Why? To allow a user to join a workspace after receiving an invite.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that accepts the invite to the workspace.
export const useWorkspaceUserAcceptInvite = () => {
  const authFetch = useAuthFetch();

  const workspaceUserAcceptInvite = async (
    workspaceId: string,
  ): Promise<UserWorkspacesType> => {
    return (await authFetch(
      `/api/workspace/user/invite/accept?workspace_id=${workspaceId}`,
      "accept workspace invite",
      {
        method: "POST",
      },
    )) as UserWorkspacesType;
  };

  return workspaceUserAcceptInvite;
};

/*
  User APIs
*/
// What? A hook for removing a user by their ID.
// Why? To remove a user from the system, typically when the user is no longer active or should be removed from the system.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that removes the user by ID.
export const useRemoveUser = () => {
  const authFetch = useAuthFetch();

  const removeUser = async (id: string): Promise<void> => {
    return await authFetch(`/api/user/remove`, "delete user", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id }),
    });
  };

  return removeUser;
};

// What? A hook for reading the current user's information.
// Why? To retrieve the details of the currently authenticated user.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the user's information.
export const useReadUser = () => {
  const authFetch = useAuthFetch();

  const readUser = async (): Promise<User> => {
    return (await authFetch(`/api/user/read`, "read user")) as User;
  };

  return readUser;
};

/*
  File APIs
*/
// What? A hook for listing files in a workspace.
// Why? To retrieve the list of files associated with a specific workspace.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the files in the workspace.
export const useListFiles = () => {
  const authFetch = useAuthFetch();

  const listFiles = async (workspaceId: string): Promise<FileType[]> => {
    return (await authFetch(
      `/api/workspace/file/list?workspace_id=${workspaceId}`,
      "list files",
    )) as FileType[];
  };

  return listFiles;
};

// What? A hook for deleting a file by its ID.
// Why? To remove a file from a workspace, typically when the file is no longer needed.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that deletes the file by ID.
export const useDeleteFile = () => {
  const authFetch = useAuthFetch();

  const deleteFile = async (workspaceId: string, fileId: string) => {
    return await authFetch(
      `/api/workspace/file/delete?workspace_id=${workspaceId}&file_path=${encodeURIComponent(fileId)}`,
      "delete file",
      {
        method: "DELETE",
      },
    );
  };

  return deleteFile;
};

/*
  File Upload API
*/
// What? A hook for uploading a file to a workspace.
// Why? To add a file to a workspace, typically for storing or processing.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that uploads the file to the workspace.
export const useUploadFile = () => {
  const authFetch = useAuthFetch();

  const uploadFile = async (
    workspaceId: string,
    file: File,
  ): Promise<FileType> => {
    const formData = new FormData();
    formData.append("file", file);

    return await authFetch(
      `/api/workspace/file/upload?workspace_id=${workspaceId}`,
      `upload file ${file.name}`,
      {
        method: "POST",
        body: formData,
      },
      30000,
    );
  };

  return uploadFile;
};

/*
  Get file bin
*/
// What? A hook for reading a citation from a file.
// Why? To retrieve detailed information about a specific citation from a file.
// How?
// - Uses the useAuthFetch hook to make the API call.
// - Returns a memoized function that fetches the citation from the file.
export const useWorkspaceCitationRead = () => {
  const authFetch = useAuthFetch("");

  const workspaceCitationRead = async (
    workspaceId: string,
    fileId: string,
  ): Promise<any> => {
    return await authFetch(
      `/api/workspace/citation/read?workspace_id=${workspaceId}&file_id=${fileId}`,
      "read file",
    );
  };

  return workspaceCitationRead;
};

export const useWorkspaceSourceFileDownload = () => {
  const authFetch = useAuthFetch("");

  const workspaceSourceFileDownload = async (
    workspaceId: string,
    fileId: string,
  ): Promise<any> => {
    return await authFetch(
      `/api/workspace/source/file/download?workspace_id=${workspaceId}&file_id=${fileId}`,
      "download file",
    );
  };

  return workspaceSourceFileDownload;
};

// What? A hook to retrieve resource usage data.
// Why? To display current resource usage and limits to the user.
// How? Returns a function that fetches mock resource usage data (to be replaced with actual API calls).
// TODO: Replace with actual API call.
export const useGetResourceUsage = () => {
  const authFetch = useAuthFetch();

  const getResourceUsage = async (): Promise<ResourceUsageData> => {
    const capacity: Record<string, any> = await authFetch(
      `/api/subscription/usage`,
      "retrieve subscription usage",
    );

    return {
      resources: [
        {
          name: "Workspaces",
          used: capacity["active_workspaces"],
          total: capacity["workspaces"],
          percentage: Math.round(
            (capacity["active_workspaces"] / capacity["workspaces"]) * 100,
          ),
          message: "",
        },
        {
          name: "Requests",
          used: capacity["used_requests"],
          total: capacity["monthly_requests"],
          percentage: Math.round(
            (capacity["used_requests"] / capacity["monthly_requests"]) * 100,
          ),
          message: "",
        },
        {
          name: "Storage",
          used: Math.round(capacity["used_storage"] * 100) / 100,
          total: Math.round(capacity["gb_storage"] * 100) / 100,
          percentage: Math.round(
            (capacity["used_storage"] / capacity["gb_storage"]) * 100,
          ),
          message: "",
        },
      ],
    };
  };
  return getResourceUsage;
};

// What? A hook to retrieve usage trend data.
// Why? To display historical usage trends to the user.
// How? Returns a function that fetches mock usage trend data (to be replaced with actual API calls).
// TODO: Replace with actual API call.
export const useGetUsageTrends = () => {
  const authFetch = useAuthFetch();

  const getUsageTrends = async (
    timeframe: UsageTrendTimeframe,
  ): Promise<UsageTrendData> => {
    // Function to calculate the date `days` before today and return it as a datetime string
    const calculateDateTime = (days: number): string => {
      const date = new Date();
      date.setDate(date.getDate() - days);
      return date.toISOString(); // Returns in "YYYY-MM-DDTHH:mm:ss.sssZ" format
    };

    let start_date: string;
    if (timeframe === "30") {
      start_date = calculateDateTime(30);
    } else if (timeframe === "90") {
      start_date = calculateDateTime(90);
    } else if (timeframe === "365") {
      start_date = calculateDateTime(365);
    } else {
      throw new Error("Invalid timeframe");
    }

    const end_date = new Date().toISOString();

    const data: number[] = await authFetch(
      `/api/subscription/usage-trends?start_date=${encodeURIComponent(start_date)}&end_date=${encodeURIComponent(end_date)}`,
      "retrieve subscription usage trends",
    );

    // Format
    const labels = data.map((_, i) => `Week ${i + 1}`);
    return {
      labels,
      datasets: [
        {
          label: "Requests Used",
          data: data,
          borderColor: "rgb(255, 111, 0)",
          backgroundColor: "rgba(255, 111, 0, 0.1)",
          tension: 0.1,
          fill: true,
        },
      ],
      options: {
        responsive: true,
        plugins: {
          legend: { position: "top" },
          title: { display: true, text: "Request Usage Over Time" },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "Requests" },
          },
          x: {
            title: { display: true, text: "Time" },
          },
        },
      },
    };
  };
  return getUsageTrends;
};

// What? A hook to fetch the Stripe subscription pricing table.
// Why? To centralize the API call for fetching subscription pricing data and ensure it uses the authenticated fetch utility.
// How?
// - Uses the useAuthFetch hook to make an authenticated API call.
// - Constructs the API URL dynamically with the provided return URL and user name.
// - Returns the subscription pricing data from the backend.
export const useFetchStripePricingTable = () => {
  const authFetch = useAuthFetch();

  const fetchStripePricingTable = async (): Promise<{
    user_id: string;
    "publishable-key": string;
    "pricing-table-id": string;
    customer_portal_url?: string;
  }> => {
    const returnUrl = encodeURIComponent(window.location.origin);
    const apiUrl = `/api/subscription/create-subscription-pricing-table?return_url=${returnUrl}`;

    try {
      const response = await authFetch(
        apiUrl,
        "fetch subscription pricing table",
        {},
      );
      return response;
    } catch (err: any) {
      console.error("Error fetching subscription pricing table:", err);
      throw err; // Rethrow for the caller to handle
    }
  };

  return fetchStripePricingTable;
};

// Biotech knowledgegraph
// TODO: Temporary hooks
export const useBKGSearch = () => {
  const authFetch = useAuthFetch();

  const bkgSearch = useCallback(
    async (
      workspaceId: string,
      params: any,
      signal?: AbortSignal,
    ): Promise<SearchBKGResults> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/search?workspace_id=${workspaceId}`,
        "search knowledge graph",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
          signal,
        },
        60000,
      )) as SearchBKGResults;

      return response;
    },
    [authFetch],
  );

  return bkgSearch;
};

export const useBKGCompanyExport = () => {
  const authFetch = useAuthFetch();

  const bkgCompanyExport = useCallback(
    async (workspaceId: string, params: any): Promise<SearchBKGResults> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/search/export?workspace_id=${workspaceId}`,
        "export companies",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
        60000,
      )) as SearchBKGResults;

      return response;
    },
    [authFetch],
  );

  return bkgCompanyExport;
};

export const useBKGVCExport = () => {
  const authFetch = useAuthFetch();

  const bkgVCExport = useCallback(
    async (workspaceId: string, params: any): Promise<SearchBKGResults> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/index/venture_capital/export?workspace_id=${workspaceId}`,
        "export venture capital",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
        60000,
      )) as SearchBKGResults;

      return response;
    },
    [authFetch],
  );

  return bkgVCExport;
};

export const useBKGCatalystEventExport = () => {
  const authFetch = useAuthFetch();

  const bkgCatalystEventExport = useCallback(
    async (workspaceId: string, params: any): Promise<SearchBKGResults> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/catalyst_event/export?workspace_id=${workspaceId}`,
        "export catalyst events",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
        60000,
      )) as SearchBKGResults;

      return response;
    },
    [authFetch],
  );

  return bkgCatalystEventExport;
};

export const useBKGCatalystEventSearch = () => {
  const authFetch = useAuthFetch();

  const bkgCatalystEventSearch = useCallback(
    async (workspaceId: string, params: any): Promise<SearchBKGResults> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/catalyst_event?workspace_id=${workspaceId}`,
        "search knowledge graph catalyst events",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
        60000,
      )) as SearchBKGResults;

      return response;
    },
    [authFetch],
  );

  return bkgCatalystEventSearch;
};

export const useBKGRead = () => {
  const authFetch = useAuthFetch();

  const bkgRead = useCallback(
    async (
      workspaceId: string,
      indexName: string,
      documentId: string,
    ): Promise<any> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/get?workspace_id=${workspaceId}&index_name=${encodeURIComponent(indexName)}&document_id=${encodeURIComponent(documentId)}`,
        `retrive ${indexName} data`,
        {
          method: "GET",
        },
      )) as any;

      return response;
    },
    [authFetch],
  );

  return bkgRead;
};

export const useBKGPDVCompanyConnections = () => {
  const authFetch = useAuthFetch();

  return useCallback(
    async (
      workspaceId: string,
      companyRef: string,
    ): Promise<PDVCompanyConnections> => {
      return (await authFetch(
        `/api/workspace/bkg/pdv/company-connections?workspace_id=${encodeURIComponent(workspaceId)}`,
        "BKG PDV company connections",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ company_ref: companyRef }),
        },
        PDV_COMPANY_CONNECTIONS_TIMEOUT_MS,
      )) as PDVCompanyConnections;
    },
    [authFetch],
  );
};

type VCPersonListResponse = {
  results: Array<{ _id: string; value: Record<string, any> }>;
};

type CompanyPersonBulkResponse = {
  results: Array<{ _id: string; value: Record<string, any> }>;
};

export const useBKGVCPersons = () => {
  const authFetch = useAuthFetch();

  const fetchVCPersons = useCallback(
    async (
      workspaceId: string,
      vcFirmId: string,
    ): Promise<VCPersonListResponse> => {
      const url = `/api/workspace/bkg/autodb/document/vc_person?workspace_id=${workspaceId}&vc_firm_id=${encodeURIComponent(vcFirmId)}`;
      const response = (await authFetch(
        url,
        "list venture capital team members",
        {
          method: "GET",
        },
      )) as VCPersonListResponse;

      return response;
    },
    [authFetch],
  );

  return fetchVCPersons;
};

export const useBKGCompanyPersonsBulk = () => {
  const authFetch = useAuthFetch();

  const fetchCompanyPersonsBulk = useCallback(
    async (
      workspaceId: string,
      references: string[],
    ): Promise<CompanyPersonBulkResponse> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/company_person/bulk?workspace_id=${workspaceId}`,
        "list company persons in bulk",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ references }),
        },
        180000,
      )) as CompanyPersonBulkResponse;

      return response;
    },
    [authFetch],
  );

  return fetchCompanyPersonsBulk;
};

export const useBKGIngest = () => {
  const authFetch = useAuthFetch();

  const bkgIngest = useCallback(
    async (workspaceId: string, data: Record<string, any>): Promise<any> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/create?workspace_id=${workspaceId}`,
        `ingest data`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      )) as any;

      return response;
    },
    [authFetch],
  );

  return bkgIngest;
};

export const useBKGIndexCompanyFilter = () => {
  const authFetch = useAuthFetch();

  const bkgIndexCompanyFilter = useCallback(
    async (workspaceId: string, signal?: AbortSignal): Promise<any> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/index/company_filter?workspace_id=${workspaceId}`,
        `get filter`,
        {
          method: "GET",
          signal,
        },
      )) as any;

      return response;
    },
    [authFetch],
  );

  return bkgIndexCompanyFilter;
};

export const useBKGIndexCatalystEventFilter = () => {
  const authFetch = useAuthFetch();

  const bkgIndexCatalystEventFilter = useCallback(
    async (workspaceId: string): Promise<any> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/index/catalyst_event_filter?workspace_id=${workspaceId}`,
        `get filter`,
        {
          method: "GET",
        },
      )) as any;

      return response;
    },
    [authFetch],
  );

  return bkgIndexCatalystEventFilter;
};

export const useBKGVCFilter = () => {
  const authFetch = useAuthFetch();

  const bkgVCFilter = useCallback(
    async (workspaceId: string, signal?: AbortSignal): Promise<any> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/index/venture_capital_filter?workspace_id=${workspaceId}`,
        `get venture capital filter`,
        {
          method: "GET",
          signal,
        },
      )) as any;

      return response;
    },
    [authFetch],
  );

  return bkgVCFilter;
};

// Names in a BKG index containing `query`, for the name filters' typeahead.
export const useBKGNameOptions = () => {
  const authFetch = useAuthFetch();

  const bkgNameOptions = useCallback(
    async (
      workspaceId: string,
      index: string,
      query: string,
      signal?: AbortSignal,
    ): Promise<string[]> => {
      const params = new URLSearchParams({
        workspace_id: workspaceId,
        index,
        q: query,
      });
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/index/name_options?${params}`,
        `get name options`,
        {
          method: "GET",
          signal,
        },
      )) as { values?: string[] };

      return response?.values ?? [];
    },
    [authFetch],
  );

  return bkgNameOptions;
};

type VentureCapitalIndexResponse = {
  total: number;
  limit: number;
  offset: number;
  items: any[];
};

export const useBKGVCIndex = () => {
  const authFetch = useAuthFetch();

  const bkgVCIndex = useCallback(
    async (
      workspaceId: string,
      {
        limit,
        offset,
        filter,
        search,
        orderBy,
        orderDir,
        signal,
      }: {
        limit?: number;
        offset?: number;
        filter?: Record<string, any> | null;
        search?: string;
        orderBy?: string;
        orderDir?: "asc" | "desc";
        signal?: AbortSignal;
      } = {},
    ): Promise<VentureCapitalIndexResponse> => {
      const params = new URLSearchParams();

      if (limit !== undefined) params.set("limit", String(limit));
      if (offset !== undefined) params.set("offset", String(offset));
      if (search) params.set("search", search);
      if (orderBy) params.set("order_by", orderBy);
      if (orderDir) params.set("order_dir", orderDir);
      if (filter && Object.keys(filter).length > 0) {
        params.set("query_filter", JSON.stringify(filter));
      }

      const query = params.toString();
      const url = query
        ? `/api/workspace/bkg/autodb/index/venture_capital?workspace_id=${workspaceId}&${query}`
        : `/api/workspace/bkg/autodb/index/venture_capital?workspace_id=${workspaceId}`;

      const response = (await authFetch(url, `list venture capital`, {
        method: "GET",
        signal,
      })) as VentureCapitalIndexResponse;

      return response;
    },
    [authFetch],
  );

  return bkgVCIndex;
};

export const useBKGCompanyInvestors = () => {
  const authFetch = useAuthFetch();

  const bkgCompanyInvestors = useCallback(
    async (
      workspaceId: string,
      companyReference: string,
    ): Promise<VentureCapitalIndexResponse> => {
      const filter = { portfolio_biotech: companyReference };
      const params = new URLSearchParams();
      params.set("query_filter", JSON.stringify(filter));
      params.set("limit", "50");

      const url = `/api/workspace/bkg/autodb/index/venture_capital?workspace_id=${workspaceId}&${params.toString()}`;

      const response = (await authFetch(url, "list company investors", {
        method: "GET",
      })) as VentureCapitalIndexResponse;

      return response;
    },
    [authFetch],
  );

  return bkgCompanyInvestors;
};

export const useBKGVCSearch = () => {
  const authFetch = useAuthFetch();

  const bkgVCSearch = useCallback(
    async (workspaceId: string, params: any): Promise<SearchBKGResults> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/document/venture_capital?workspace_id=${workspaceId}`,
        `search venture capital`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        },
        60000,
      )) as SearchBKGResults;

      return response;
    },
    [authFetch],
  );

  return bkgVCSearch;
};

/*
  Ingestion Queue APIs
*/
export type IngestionQueueItem = {
  _id: string;
  status: "pending" | "completed" | "error" | "ignored";
  processed: boolean;
  method: "manual" | "agent";
  ingestion_date: string;
  processing_date?: string;
  index_name?: string;
  name?: string;
};

export type IngestionQueueResponse = {
  total: number;
  pending: number;
  completed: number;
  error: number;
  ignored: number;
  items: IngestionQueueItem[];
};

export type IngestionScheduleResponse = {
  next_scheduled_run: string | null;
  last_indexing_completed: string | null;
  pending_items: number;
  scheduled_interval_hours: number;
};

export type TriggerIndexingResponse = {
  status: string;
  message: string;
};

export const useBKGIngestionQueue = () => {
  const authFetch = useAuthFetch();

  const bkgIngestionQueue = useCallback(
    async (
      workspaceId: string,
      options?: { limit?: number; showProcessed?: boolean },
    ): Promise<IngestionQueueResponse> => {
      const params = new URLSearchParams();
      if (options?.limit !== undefined)
        params.set("limit", String(options.limit));
      if (options?.showProcessed !== undefined)
        params.set("show_processed", String(options.showProcessed));

      const query = params.toString();
      const url = query
        ? `/api/workspace/bkg/autodb/ingestion/queue?workspace_id=${workspaceId}&${query}`
        : `/api/workspace/bkg/autodb/ingestion/queue?workspace_id=${workspaceId}`;

      const response = (await authFetch(url, "get ingestion queue", {
        method: "GET",
      })) as IngestionQueueResponse;

      return response;
    },
    [authFetch],
  );

  return bkgIngestionQueue;
};

export const useBKGIngestionSchedule = () => {
  const authFetch = useAuthFetch();

  const bkgIngestionSchedule = useCallback(
    async (workspaceId: string): Promise<IngestionScheduleResponse> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/ingestion/schedule?workspace_id=${workspaceId}`,
        "get ingestion schedule",
        {
          method: "GET",
        },
      )) as IngestionScheduleResponse;

      return response;
    },
    [authFetch],
  );

  return bkgIngestionSchedule;
};

export const useBKGTriggerIndexing = () => {
  const authFetch = useAuthFetch();

  const bkgTriggerIndexing = useCallback(
    async (workspaceId: string): Promise<TriggerIndexingResponse> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/ingestion/trigger?workspace_id=${workspaceId}`,
        "trigger indexing",
        {
          method: "POST",
        },
      )) as TriggerIndexingResponse;

      return response;
    },
    [authFetch],
  );

  return bkgTriggerIndexing;
};

/*
  Field Indexing Progress APIs
*/
export type CurrentEntityInfo = {
  id: string;
  name: string;
  index_name: string;
  field_name: string | null;
};

export type IndexingStats = {
  total_entities: number;
  completed_entities: number;
  total_fields: number;
  completed_fields: number;
  errors: number;
};

export type RecentIndexedItem = {
  entity_id: string;
  entity_name: string;
  index_name: string;
  field_name: string;
  status: "success" | "error" | "skipped";
  completed_at: string;
  error_message: string | null;
};

export type IndexingProgressResponse = {
  workspace_id: string;
  status: "idle" | "running" | "completed" | "error";
  started_at: string | null;
  completed_at: string | null;
  current_entity: CurrentEntityInfo | null; // Backwards compat: first active
  current_entities: CurrentEntityInfo[]; // All active (for parallel processing)
  index_name: string | null;
  stats: IndexingStats;
  recent_items: RecentIndexedItem[];
  error_message: string | null;
};

export const useBKGIndexingProgress = () => {
  const authFetch = useAuthFetch();

  const bkgIndexingProgress = useCallback(
    async (workspaceId: string): Promise<IndexingProgressResponse> => {
      const response = (await authFetch(
        `/api/workspace/bkg/autodb/indexing/progress?workspace_id=${workspaceId}`,
        "get indexing progress",
        {
          method: "GET",
        },
      )) as IndexingProgressResponse;

      return response;
    },
    [authFetch],
  );

  return bkgIndexingProgress;
};

/*
  Digest APIs
*/

export const useDigestConfigList = () => {
  const authFetch = useAuthFetch();

  const digestConfigList = useCallback(
    async (workspaceId: string): Promise<DigestConfig[]> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/configs?workspace_id=${workspaceId}`,
        "list digest configs",
        { method: "GET" },
      )) as DigestConfig[];
    },
    [authFetch],
  );

  return digestConfigList;
};

export const useDigestConfigGet = () => {
  const authFetch = useAuthFetch();

  const digestConfigGet = useCallback(
    async (workspaceId: string, configId: string): Promise<DigestConfig> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/configs/${configId}?workspace_id=${workspaceId}`,
        "get digest config",
        { method: "GET" },
      )) as DigestConfig;
    },
    [authFetch],
  );

  return digestConfigGet;
};

export const useDigestConfigCreate = () => {
  const authFetch = useAuthFetch();

  const digestConfigCreate = useCallback(
    async (
      workspaceId: string,
      data: DigestConfigCreate,
    ): Promise<DigestConfig> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/configs?workspace_id=${workspaceId}`,
        "create digest config",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      )) as DigestConfig;
    },
    [authFetch],
  );

  return digestConfigCreate;
};

export const useDigestConfigUpdate = () => {
  const authFetch = useAuthFetch();

  const digestConfigUpdate = useCallback(
    async (
      workspaceId: string,
      configId: string,
      data: DigestConfigUpdate,
    ): Promise<DigestConfig> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/configs/${configId}?workspace_id=${workspaceId}`,
        "update digest config",
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      )) as DigestConfig;
    },
    [authFetch],
  );

  return digestConfigUpdate;
};

export const useDigestConfigDelete = () => {
  const authFetch = useAuthFetch();

  const digestConfigDelete = useCallback(
    async (workspaceId: string, configId: string): Promise<void> => {
      await authFetch(
        `/api/workspace/bkg/digest/configs/${configId}?workspace_id=${workspaceId}`,
        "delete digest config",
        { method: "DELETE" },
      );
    },
    [authFetch],
  );

  return digestConfigDelete;
};

export const useDigestGenerate = () => {
  const authFetch = useAuthFetch();

  const digestGenerate = useCallback(
    async (workspaceId: string, configId: string): Promise<DigestResult> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/configs/${configId}/generate?workspace_id=${workspaceId}`,
        "generate digest",
        { method: "POST" },
        300000,
      )) as DigestResult;
    },
    [authFetch],
  );

  return digestGenerate;
};

export const useDigestResultList = () => {
  const authFetch = useAuthFetch();

  const digestResultList = useCallback(
    async (workspaceId: string, configId: string): Promise<DigestResult[]> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/configs/${configId}/results?workspace_id=${workspaceId}`,
        "list digest results",
        { method: "GET" },
      )) as DigestResult[];
    },
    [authFetch],
  );

  return digestResultList;
};

export const useDigestResultGet = () => {
  const authFetch = useAuthFetch();

  const digestResultGet = useCallback(
    async (workspaceId: string, resultId: string): Promise<DigestResult> => {
      return (await authFetch(
        `/api/workspace/bkg/digest/results/${resultId}?workspace_id=${workspaceId}`,
        "get digest result",
        { method: "GET" },
      )) as DigestResult;
    },
    [authFetch],
  );

  return digestResultGet;
};
